import { useState } from 'react';
// eslint-disable-next-line import/named
/* eslint-disable indent */
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@trip-planner-monorepo/shared';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const testFirebaseConnection = async () => {
    try {
      console.log('Testing Firebase connectivity...');

      // Test auth state listener
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe(); // Cleanup listener right away
          console.log('Firebase Auth is accessible, current auth state:', { isAuthenticated: !!user });
          resolve({
            success: true,
            authState: !!user,
            message: 'Firebase Auth is accessible'
          });
        }, (error) => {
          console.error('Firebase Auth error:', error);
          resolve({
            success: false,
            error: error.message,
            message: 'Failed to access Firebase Auth'
          });
        });

        // Set a timeout in case the auth state change never fires
        setTimeout(() => {
          unsubscribe();
          resolve({
            success: false,
            error: 'Timeout waiting for Firebase Auth response',
            message: 'Firebase Auth connection timed out'
          });
        }, 5000);
      });
    } catch (error) {
      console.error('Firebase connectivity test failed:', error);
      return {
        success: false,
        error: error.message,
        message: 'Failed to test Firebase connectivity'
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Login attempt starting...', { email, isRegistering });
    setError('');
    setLoading(true);

    try {
      console.log('Attempting Firebase authentication...');
      if (isRegistering) {
        console.log('Creating new user account...');
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log('Successfully created user:', userCredential.user.uid);
      } else {
        console.log('Signing in existing user...');
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('Successfully logged in user:', userCredential.user.uid);
      }
      console.log('Authentication successful, navigating to home...');
      navigate('/');
    } catch (error) {
      console.error('Detailed auth error:', {
        code: error.code,
        message: error.message,
        fullError: error
      });

      if (error.code === 'auth/network-request-failed') {
        console.log('Running Firebase connectivity test...');
        const testResult = await testFirebaseConnection();
        console.log('Connectivity test results:', testResult);
      }

      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/weak-password':
          setError('Password should be at least 6 characters');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Invalid email or password');
          break;
        case 'auth/network-request-failed':
          setError(`Network error (${error.code}). Ensure the device has internet access (not just LAN) and no firewalls are blocking Google/Firebase.`);
          break;
        default:
          setError(isRegistering
            ? `Failed to create account. (${error.code})`
            : `Failed to log in. (${error.code})`);
      }
    } finally {
      console.log('Login attempt completed');
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <h2>{isRegistering ? 'Register' : 'Login'}</h2>
        {error && <p className="error-message">{error}</p>}
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-container">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle-button"
              onClick={togglePasswordVisibility}
              disabled={loading}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading} className="submit-button">
          {loading ? (
            isRegistering ? 'Creating Account...' : 'Logging In...'
          ) : (
            isRegistering ? 'Register' : 'Login'
          )}
        </button>
        <button
          type="button"
          onClick={() => setIsRegistering(!isRegistering)}
          className="toggle-button"
          disabled={loading}
        >
          {isRegistering ? 'Switch to Login' : 'Switch to Register'}
        </button>
      </form>
    </div>
  );
}

export default Login;